// frontend/src/pages/member/MemberDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MemberLayout from './MemberLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MemberDashboard = () => {
  const navigate = useNavigate();
  const [memberData, setMemberData] = useState(null);
  const [balances, setBalances] = useState({
    shares: 0,
    normalLoan: 0,
    schoolLoan: 0,
    emergencyLoan: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMemberData();
  }, []);

  const fetchMemberData = async () => {
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const storedUser = localStorage.getItem('hopeSaccoUser');
      
      if (!token || !storedUser) {
        navigate('/login');
        return;
      }

      const userData = JSON.parse(storedUser);
      
      // Allow member, admin, and official roles to access member dashboard
      const allowedRoles = ['member', 'admin', 'official'];
      if (!allowedRoles.includes(userData.role)) {
        navigate('/login');
        return;
      }

      // Get member ID from user profile
      const memberId = userData.profile?.memberId;
      
      if (!memberId) {
        setError('Member ID not found. Please contact administrator.');
        setIsLoading(false);
        return;
      }

      // Fetch member profile
      const memberResponse = await fetch(`${API_URL}/members/by-member-id/${memberId}/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!memberResponse.ok) {
        throw new Error('Failed to fetch member data');
      }

      const memberResult = await memberResponse.json();
      
      if (!memberResult.success) {
        throw new Error(memberResult.message || 'Failed to load member data');
      }

      setMemberData(memberResult.data.member);

      // Fetch current balances from monthly overview
      await fetchCurrentBalances(memberId, token);

    } catch (error) {
      console.error('Error fetching member data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentBalances = async (memberId, token) => {
    try {
      const now = new Date();
      // Use previous month — current month contributions are not yet made
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const currentMonth = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;

      const response = await fetch(
        `${API_URL}/reports/monthly-overview?month=${currentMonth}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data && data.data.members) {
          const memberData = data.data.members.find(m => m.memberId === memberId);
          
          if (memberData && memberData.monthData) {
            const md = memberData.monthData;
            setBalances({
              shares: md.shares || 0,
              normalLoan: md.loanBalance || 0,
              schoolLoan: md.schoolFeesLoan || 0,
              emergencyLoan: md.emergencyLoan || 0
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching balances:', error);
      // Use fallback from member document if available
      if (memberData?.currentBalances) {
        const cb = memberData.currentBalances;
        setBalances({
          shares: cb.shares || 0,
          normalLoan: cb.normalLoanBalance || cb.normalLoan || 0,
          schoolLoan: cb.schoolLoanBalance || cb.schoolLoan || cb.schoolFeesLoan || 0,
          emergencyLoan: cb.emergencyLoanBalance || cb.emergencyLoan || 0
        });
      }
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'Unknown';
    try {
      let date;
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else {
        date = new Date(dateValue);
      }
      
      if (isNaN(date.getTime())) return 'Unknown';
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Unknown';
    }
  };

  const formatCurrency = (amount) => {
    return amount.toLocaleString(undefined, { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  if (isLoading) {
    return (
      <MemberLayout activePage="dashboard" pageTitle="My Dashboard">
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>Loading your dashboard...</p>
        </div>
      </MemberLayout>
    );
  }

  if (error || !memberData) {
    return (
      <MemberLayout activePage="dashboard" pageTitle="My Dashboard">
        <div className="error-page">
          <h2>Error</h2>
          <p>{error || 'Failed to load dashboard'}</p>
        </div>
      </MemberLayout>
    );
  }

  const totalLoans = balances.normalLoan + balances.schoolLoan + balances.emergencyLoan;

  return (
    <MemberLayout activePage="dashboard" pageTitle="My Dashboard">
      <div className="dashboard-container">
        {/* Welcome Section */}
        <div className="welcome-section" style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '2rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.75rem' }}>
            Welcome back, {memberData.personalInfo?.fullName || 'Member'}! 👋
          </h2>
          <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9 }}>
            Member ID: {memberData.memberId} | Joined: {formatDate(memberData.saccoInfo?.dateJoined)}
          </p>
        </div>

        {/* Balance Cards */}
        <div className="balance-cards">
          <div className="balance-card shares">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <div className="card-label">Share Balance</div>
              <div className="card-value">KES {formatCurrency(balances.shares)}</div>
            </div>
          </div>

          <div className="balance-card loan normal">
            <div className="card-icon">🏦</div>
            <div className="card-content">
              <div className="card-label">Normal Loan</div>
              <div className="card-value">KES {formatCurrency(balances.normalLoan)}</div>
            </div>
          </div>

          <div className="balance-card loan school">
            <div className="card-icon">🎓</div>
            <div className="card-content">
              <div className="card-label">School Loan</div>
              <div className="card-value">KES {formatCurrency(balances.schoolLoan)}</div>
            </div>
          </div>

          <div className="balance-card loan emergency">
            <div className="card-icon">🚨</div>
            <div className="card-content">
              <div className="card-label">Emergency Loan</div>
              <div className="card-value">KES {formatCurrency(balances.emergencyLoan)}</div>
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '1.5rem',
          marginTop: '2rem'
        }}>
          {/* Account Summary */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Account Summary</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: '#f8f9fa', borderRadius: '6px' }}>
                <span style={{ fontWeight: '500' }}>Total Shares:</span>
                <span style={{ color: '#28a745', fontWeight: 'bold' }}>KES {formatCurrency(balances.shares)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: '#f8f9fa', borderRadius: '6px' }}>
                <span style={{ fontWeight: '500' }}>Total Loans:</span>
                <span style={{ color: totalLoans > 0 ? '#dc3545' : '#6c757d', fontWeight: 'bold' }}>
                  KES {formatCurrency(totalLoans)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', background: '#e3f2fd', borderRadius: '6px' }}>
                <span style={{ fontWeight: '500' }}>Net Position:</span>
                <span style={{ fontWeight: 'bold', color: balances.shares - totalLoans >= 0 ? '#28a745' : '#dc3545' }}>
                  KES {formatCurrency(balances.shares - totalLoans)}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Quick Actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button 
                className="btn btn-primary" 
                onClick={() => navigate('/member/profile')}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                👤 View My Profile
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => navigate('/member/loans')}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                💵 View My Loans
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => navigate('/member/statement')}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                📄 Download Statement
              </button>
            </div>
          </div>
        </div>

        {/* Member Information */}
        <div className="card" style={{ marginTop: '2rem' }}>
          <h3 style={{ marginTop: 0 }}>Contact Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            <div>
              <div style={{ color: '#666', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Phone Number</div>
              <div style={{ fontWeight: '500' }}>{memberData.personalInfo?.phoneNumber || 'Not provided'}</div>
            </div>
            <div>
              <div style={{ color: '#666', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Email</div>
              <div style={{ fontWeight: '500' }}>{memberData.personalInfo?.email || 'Not provided'}</div>
            </div>
            <div>
              <div style={{ color: '#666', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Status</div>
              <div>
                <span className={`status-badge status-${memberData.saccoInfo?.status?.toLowerCase()}`}>
                  {memberData.saccoInfo?.status || 'Unknown'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MemberLayout>
  );
};

export default MemberDashboard;
